/**
 * src/components/admin/AdminNav.js
 * 
 * Admin sidebar/top navigation component.
 * Mobile: collapsible top menu with hamburger
 * Desktop: persistent left sidebar
 */

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Bell,
  Users,
  TicketCheck,
  MessageSquareText,
  LifeBuoy,
  Activity,
  Shield,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  FileWarning,
  AlertTriangle,
  ScrollText,
  Megaphone,
} from "lucide-react";
import { useAdmin } from "../../hooks/useAdmin";

const NAV_ITEMS = [
  {
    id: "dashboard",
    label: "Dashboard",
    to: "/admin",
    Icon: LayoutDashboard,
  },
  {
    id: "notifications",
    label: "Notifications",
    to: "/admin/notifications",
    Icon: Bell,
  },
  {
    id: "users",
    label: "User Management",
    to: "/admin/users",
    Icon: Users,
  },
  {
    id: "tickets",
    label: "Support Tickets",
    to: "/admin/tickets",
    Icon: TicketCheck,
  },
  {
    id: "support-chat",
    label: "Support Chat",
    to: "/admin/support-chat",
    Icon: LifeBuoy,
  },
  {
    id: "feedback",
    label: "Feedback",
    to: "/admin/feedback",
    Icon: MessageSquareText,
  },
  {
    id: "moderation",
    label: "Content Moderation",
    to: "/admin/moderation",
    Icon: FileWarning,
  },
  {
    id: "reports",
    label: "Reports",
    to: "/admin/reports",
    Icon: AlertTriangle,
  },
  {
    id: "analytics",
    label: "System Health",
    to: "/admin/analytics",
    Icon: Activity,
  },
  {
    id: "activity-log",
    label: "Activity Log",
    to: "/admin/activity",
    Icon: ScrollText,
  },
];

function NavItem({ item, isActive, onClick }) {
  const { Icon, label, to } = item;

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150
        ${isActive
          ? "bg-green-600 text-white shadow-sm"
          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
        }
      `}
    >
      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

export default function AdminNav() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isSuperAdmin } = useAdmin();

  const currentPath = location.pathname;

  const isActive = (to) => {
    if (to === "/admin") return currentPath === "/admin";
    return currentPath.startsWith(to);
  };

  const navContent = (
    <nav className="flex flex-col gap-1">
      {NAV_ITEMS.map((item) => (
        <NavItem
          key={item.id}
          item={item}
          isActive={isActive(item.to)}
          onClick={() => setMobileOpen(false)}
        />
      ))}
    </nav>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 min-h-0 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 dark:border-neutral-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center">
              {isSuperAdmin ? (
                <ShieldCheck className="w-5 h-5 text-white" />
              ) : (
                <Shield className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">Admin Panel</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isSuperAdmin ? "Super Admin" : "Administrator"}
              </p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <div className="flex-1 overflow-y-auto p-3">
          {navContent}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-neutral-800">
          <Link
            to="/farm"
            className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition"
          >
            <ChevronRight className="w-3.5 h-3.5" />
            Back to Orrith
          </Link>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="flex items-center justify-between px-3 h-12">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-green-600 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-bold text-gray-900 dark:text-white">Admin</span>
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
            aria-label="Toggle admin menu"
          >
            {mobileOpen ? (
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>

        {/* Mobile Dropdown */}
        {mobileOpen && (
          <div className="px-3 pb-3 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 shadow-lg">
            {navContent}
            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-neutral-800">
              <Link
                to="/farm"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-green-600"
              >
                <ChevronRight className="w-3.5 h-3.5" />
                Back to Orrith
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
